/*
 * Export builder page.
 *
 * The export-composition surface. Scheduling, large-table downloads and the
 * export history each live on their own page under the Exporting nav menu
 * (ScheduledExportsPage / DataTablesPage / ExportHistoryPage); this page is
 * just compose + preview + pull.
 *
 *   1. Templates - provided layouts plus the customer's own saved layouts.
 *                  Applying one sets the section list, format and title;
 *                  "Save as template" persists the current build.
 *   2. Build     - left rail ticks the selectable data blocks (grouped by
 *                  source page); right rail orders them, picks the format and
 *                  downloads or emails. Every block carries real data, and
 *                  each pull is logged to the export history.
 *   3. Preview   - opens in a modal (Preview button) so it never crowds the
 *                  build surface; renders the chosen format using that real
 *                  data, so what the customer sees is what the file contains.
 *
 * No client-side computation beyond formatting; the resolved blocks come from
 * exportData.js. See that file + exportGenerators.js for the data + file
 * contract, and the Postgres mapping for each persisted surface.
 */
import React, { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, TextField, Divider, Snackbar, Tooltip, Dialog, DialogContent, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import CoPresentOutlinedIcon from '@mui/icons-material/CoPresentOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import MainCard from '../../../components/cards/MainCard';
import { ORG_SCORE, ORG_BAND, BENCHMARK, bandFor } from '../riskScore';
import useFeature from '../useFeature';
import {
    COMPONENT_LIBRARY, SECTION_INDEX, FORMATS, EXPORT_PERIODS, periodLabel, PROVIDED_TEMPLATES, INPUT_SX,
    assembleSections, slugify, downloadBlob, logExport,
} from '../exportData';
import { useGetExportTemplatesQuery, useSaveExportTemplatesMutation } from '../../../store/services/reportLayout.service';
import { generateDocument, documentFilename } from '../exportGenerators';
import ExportPreview from './ExportPreview';
import useDashboardData, { resolveDateRange } from '../hooks/useDashboardData';
import { useGetDwmBreachStatsQuery, useGetDwmBreachTrendQuery, useGetDwmBreachedUsersQuery } from '../../../store/services/dwm-service';

const DEFAULT_ORDER = ['home.risk-dial', 'home.risk-trend', 'text.implication', 'home.top-risk'];

// Breach-trend bucket keys arrive as "YYYY-MM" (month), "YYYY-Www" (ISO week)
// or "YYYY-MM-DD" (day); format a compact display label per granularity.
const DWM_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDwmBucket(bucket, granularity) {
    const b = String(bucket || '');
    if (granularity === 'week') return b.replace(/^\d{4}-W?/, 'wk ');
    if (granularity === 'day') return b.slice(5);
    const m = parseInt(b.slice(5, 7), 10);
    return `${DWM_MONTHS[m - 1] || b} ${b.slice(2, 4)}`;
}

// Provided templates render a real MUI icon (verified to exist in the project)
// keyed by id; saved custom templates fall back to the default.
const TEMPLATE_ICONS = {
    'board-pack':     SummarizeOutlinedIcon,
    'exec-review':    CoPresentOutlinedIcon,
    'monthly-ops':    EventRepeatOutlinedIcon,
    'compliance':     FactCheckOutlinedIcon,
    'risk-deep-dive': TroubleshootOutlinedIcon,
};
const templateIcon = (id) => TEMPLATE_ICONS[id] || DashboardCustomizeOutlinedIcon;

// Compact, keyboard-accessible tick row. Replaces the bulky MUI Checkbox +
// FormControlLabel so the two-column data list stays dense. The 15px square
// fills teal when on; uses the .rbx-root CSS tokens so it tracks the theme.
export function TickRow({ checked, onToggle, label, indent }) {
    return (
        <Box
            role="checkbox" aria-checked={checked} tabIndex={0}
            onClick={onToggle}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
            sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, ml: indent ? '24px' : 0,
                px: 0.75, py: '3px', borderRadius: '4px', cursor: 'pointer', userSelect: 'none', transition: 'background 120ms',
                '&:hover': { background: 'var(--surface2)' },
                '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.secondary.main}`, outlineOffset: '-2px' },
            }}
        >
            <Box aria-hidden="true" sx={{
                flexShrink: 0, width: 15, height: 15, borderRadius: '4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1.5px solid', borderColor: checked ? 'secondary.main' : 'var(--border2)',
                bgcolor: checked ? 'secondary.main' : 'transparent', transition: 'background 120ms, border-color 120ms',
            }}>
                {checked && (
                    <Box component="svg" viewBox="0 0 16 16" sx={{ width: 11, height: 11, display: 'block' }}>
                        <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="#fff" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
                    </Box>
                )}
            </Box>
            <Typography sx={{ fontSize: 11, lineHeight: 1.25, color: checked ? 'var(--text)' : 'var(--muted)', fontWeight: checked ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {label}
            </Typography>
        </Box>
    );
}

// One row of pill buttons, single selection. Options are [{ id, label }].
function PillTabs({ options, value, onChange }) {
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {options.map((o) => {
                const active = value === o.id;
                return (
                    <Button
                        key={o.id} size="small" onClick={() => onChange(o.id)}
                        sx={{
                            fontSize: 11, textTransform: 'none', minWidth: 0, px: 1.25, py: '3px', borderRadius: '4px',
                            border: '1px solid', borderColor: active ? 'secondary.main' : 'divider',
                            bgcolor: active ? 'secondary.main' : 'transparent',
                            color: active ? 'common.white' : 'text.secondary',
                            '&:hover': { borderColor: 'secondary.main', bgcolor: active ? 'secondary.main' : 'transparent', color: active ? 'common.white' : 'secondary.main' },
                        }}
                    >{o.label}</Button>
                );
            })}
        </Box>
    );
}

const FORMAT_OPTIONS = FORMATS.map((f) => ({ id: f, label: f }));
function FormatTabs({ value, onChange }) {
    return <PillTabs options={FORMAT_OPTIONS} value={value} onChange={onChange} />;
}

function TemplateCard({ tpl, active, onApply, onDelete }) {
    const Icon = templateIcon(tpl.id);
    return (
        <Box
            onClick={onApply}
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onApply(); } }}
            sx={{
                position: 'relative', cursor: 'pointer', flex: '0 0 auto', width: 190,
                border: '1px solid', borderColor: active ? 'secondary.main' : 'divider', borderRadius: 1.5, p: 1.25,
                bgcolor: active ? 'action.selected' : 'background.paper', transition: 'border-color 150ms, background 150ms',
                '&:hover': { borderColor: 'secondary.main' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Icon sx={{ fontSize: 17, color: active ? 'secondary.main' : 'text.secondary' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tpl.name}</Typography>
                {tpl.custom && (
                    <Tooltip title="Delete template">
                        <Button onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ minWidth: 0, p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </Button>
                    </Tooltip>
                )}
            </Box>
            <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.4, mb: 0.5, minHeight: 28 }}>{tpl.description || `${tpl.sections.length} sections`}</Typography>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tpl.format} · {tpl.sections.length} sections</Typography>
        </Box>
    );
}

// The Dialog portals into .rbx-root (not document.body) so the reports-b CSS
// custom properties the preview relies on (var(--bar-track)/--teal/etc.)
// resolve; otherwise the bars would render colourless inside the modal.
const rbxContainer = () => (typeof document !== 'undefined' ? document.querySelector('.rbx-root') : null);

function PreviewDialog({ open, onClose, format, setFormat, title, sections, brand, periodText, onDownload }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth container={rbxContainer} aria-labelledby="export-preview-title" PaperProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper', maxHeight: '92vh' } }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography id="export-preview-title" sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>Live preview</Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{title || 'Untitled'} · {sections.length} section{sections.length === 1 ? '' : 's'} · last {periodText} · real data, this is what downloads</Typography>
                </Box>
                <FormatTabs value={format} onChange={setFormat} />
                <IconButton size="small" onClick={onClose} aria-label="Close preview"><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, bgcolor: 'action.hover', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                <ExportPreview format={format} title={title} sections={sections} brand={brand} periodLabel={periodText} />
            </DialogContent>
            <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={onClose} size="small" sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}>Close</Button>
                <Button onClick={onDownload} variant="contained" color="secondary" size="small" startIcon={<DownloadIcon sx={{ fontSize: 14 }} />} disabled={sections.length === 0} sx={{ textTransform: 'none', fontSize: 12 }}>Download {format}</Button>
            </Box>
        </Dialog>
    );
}

export default function ExportBuilderPage() {
    const benchmarkOn = useFeature('industryBenchmarking');
    const dwmOn       = useFeature('darkWebMonitoring');
    // Documents are branded with the tenancy's own name (white-label friendly);
    // fall back to Boxphish only if the slice hasn't been populated yet.
    const { tenancyId, tenancyName } = useSelector((state) => state.tenancy);
    const brand = (tenancyName || '').trim() || 'Boxphish';

    const [order, setOrder] = useState(DEFAULT_ORDER);
    const [withDrill, setWithDrill] = useState(new Set());
    const [title, setTitle] = useState('Q2 board report');
    const [format, setFormat] = useState('PDF');
    const [period, setPeriod] = useState('12m');
    const [emailTo, setEmailTo] = useState('');
    const [activeTemplate, setActiveTemplate] = useState('board-pack');
    // Saved templates live server-side per tenancy (ReportLayouts table,
    // SK EXPORT_TEMPLATES) so they follow the user across browsers and are
    // shared with colleagues in the same tenancy. RTK re-fetches on tenancy
    // switch; add/delete both PUT the full next list.
    const templatesQ = useGetExportTemplatesQuery(tenancyId, { skip: tenancyId == null });
    const customTemplates = useMemo(
        () => (templatesQ.data?.templates ?? []).map((t) => ({ ...t, custom: true })),
        [templatesQ.data],
    );
    const [saveExportTemplates] = useSaveExportTemplatesMutation();
    const [savingName, setSavingName] = useState(null); // null = not saving, string = name input
    const [previewOpen, setPreviewOpen] = useState(false);
    const [toast, setToast] = useState('');

    // Only the aggregations the SELECTED sections need are fetched (others are
    // skipped), so opening the builder no longer fires all 12 at once. Doc tables
    // cap at 20 rows, so limits stay small: the roster embeds its grand total and
    // the users agg is score-sorted, so a short top-N is still exact.
    const need = useMemo(() => {
        const S = new Set(order);
        const any = (...ids) => ids.some((id) => S.has(id));
        return {
            risk: any('home.risk-dial', 'risk.methodology', 'risk.factors', 'text.implication'),
            benchmark: benchmarkOn && any('home.risk-dial', 'text.implication'),
            riskTrend: any('home.risk-trend'),
            summary: any('home.metrics'),
            sims: any('sims.metrics', 'sims.funnel', 'sims.by-sim'),
            trends: any('sims.clicks'),
            device: any('sims.device'),
            leagues: any('sims.dept'),
            courses: any('courses.metrics', 'courses.by-course'),
            courseDetail: any('courses.metrics', 'courses.outstanding'),
            users: any('home.top-risk', 'text.implication'),
            dwmTrend: dwmOn && any('dwm.trend'),
            dwmByEmail: dwmOn && any('dwm.by-email'),
        };
    }, [order, benchmarkOn, dwmOn]);
    const riskQ      = useDashboardData({ metric: 'simulations', aggregation: 'risk', period }, { skip: !need.risk });
    const benchQ     = useDashboardData({ metric: 'simulations', aggregation: 'risk_benchmark', period }, { skip: !need.benchmark });
    const trendQ     = useDashboardData({ metric: 'simulations', aggregation: 'risk_trend', period }, { skip: !need.riskTrend });
    const summaryQ   = useDashboardData({ metric: 'simulations', aggregation: 'summary', period }, { skip: !need.summary });
    const simsQ      = useDashboardData({ metric: 'simulations', aggregation: 'sims', period }, { skip: !need.sims });
    const trendsQ    = useDashboardData({ metric: 'simulations', aggregation: 'trends', period }, { skip: !need.trends });
    const deviceQ    = useDashboardData({ metric: 'simulations', aggregation: 'sim_device', period }, { skip: !need.device });
    const leaguesQ   = useDashboardData({ metric: 'simulations', aggregation: 'leagues', period }, { skip: !need.leagues });
    const coursesQ   = useDashboardData({ metric: 'courses', aggregation: 'courses', period }, { skip: !need.courses });
    const courseDetQ = useDashboardData({ metric: 'courses', aggregation: 'course_detail', limit: 50, period }, { skip: !need.courseDetail });
    const highRiskQ  = useDashboardData({ metric: 'simulations', aggregation: 'users', limit: 100, period }, { skip: !need.users });
    // DWM pulls go straight to the dwm-api (same endpoints as the Dark web
    // page), windowed to the selected export period.
    const dwmRange   = useMemo(() => resolveDateRange({ period }), [period]);
    // Fetched whenever DWM is on (not just when the strip is selected): the
    // Snapshot used card shows the live credential-leak count too.
    const dwmStatsQ  = useGetDwmBreachStatsQuery({ tenancyId, startDate: dwmRange.start, endDate: dwmRange.end }, { skip: !dwmOn || !tenancyId });
    const dwmTrendQ  = useGetDwmBreachTrendQuery({ tenancyId, startDate: dwmRange.start, endDate: dwmRange.end, granularity: 'auto' }, { skip: !need.dwmTrend || !tenancyId });
    const dwmEmailsQ = useGetDwmBreachedUsersQuery({ tenancyId, page: 1, limit: 100, startDate: dwmRange.start, endDate: dwmRange.end }, { skip: !need.dwmByEmail || !tenancyId });
    const live = useMemo(() => ({
        risk: riskQ.data?.risk,
        benchmark: benchQ.data?.riskBenchmark,
        riskTrend: trendQ.data?.riskTrend,
        summary: summaryQ.data?.performance,
        sims: simsQ.data?.sims,
        trends: trendsQ.data?.trends,
        simDevices: deviceQ.data?.simDevices,
        leagues: leaguesQ.data?.leagues,
        courses: coursesQ.data?.courses,
        courseDetail: courseDetQ.data?.courseDetail,
        riskUsers: highRiskQ.data?.riskUsers,
        dwmStats: dwmStatsQ.data,
        dwmTrend: (dwmTrendQ.data?.points || []).map((r) => ({
            label: fmtDwmBucket(r.bucket, dwmTrendQ.data?.granularity || 'month'),
            value: Number(r.mentions) || 0,
        })),
        dwmByEmail: [...(dwmEmailsQ.data?.users || [])]
            .sort((a, b) => (b.breaches ?? 0) - (a.breaches ?? 0))
            .map((u) => ({
                email: u.email,
                mentions: u.breaches ?? 0,
                withCreds: u.passwordAvailable ?? 0,
                latest: u.latestBreachAt
                    ? new Date(`${u.latestBreachAt}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                    : '-',
            })),
    }), [riskQ.data, benchQ.data, trendQ.data, summaryQ.data, simsQ.data, trendsQ.data, deviceQ.data, leaguesQ.data, coursesQ.data, courseDetQ.data, highRiskQ.data, dwmStatsQ.data, dwmTrendQ.data, dwmEmailsQ.data]);
    const liveLoading = [riskQ, benchQ, trendQ, summaryQ, simsQ, trendsQ, deviceQ, leaguesQ, coursesQ, courseDetQ, highRiskQ, dwmStatsQ, dwmTrendQ, dwmEmailsQ].some((qq) => qq.isFetching);

    const selected = useMemo(() => new Set(order), [order]);
    const showToast = useCallback((msg) => setToast(msg), []);

    // Library filtered by tenancy feature flags so off features never offer a
    // block that would render empty.
    const library = useMemo(
        () => COMPONENT_LIBRARY.filter((g) => !g.feature || (g.feature === 'darkWebMonitoring' ? dwmOn : true)),
        [dwmOn],
    );

    const toggle = (id) => {
        setActiveTemplate(null);
        setOrder((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
        setWithDrill((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev); next.delete(id); return next;
        });
    };

    const toggleDrill = (id) => {
        setActiveTemplate(null);
        setWithDrill((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const move = (id, dir) => {
        setActiveTemplate(null);
        setOrder((prev) => {
            const i = prev.indexOf(id);
            const j = i + dir;
            if (i < 0 || j < 0 || j >= prev.length) return prev;
            const next = [...prev];
            [next[i], next[j]] = [next[j], next[i]];
            return next;
        });
    };

    const applyTemplate = (tpl) => {
        const sections = tpl.sections.filter((id) => {
            const meta = SECTION_INDEX[id];
            if (!meta) return false;
            if (meta.feature === 'darkWebMonitoring' && !dwmOn) return false;
            return true;
        });
        setOrder(sections);
        setWithDrill(new Set((tpl.drill || []).filter((id) => sections.includes(id))));
        setTitle(tpl.title || tpl.name);
        // Old templates may carry the retired CSV format - fall back to PDF.
        setFormat(FORMATS.includes(tpl.format) ? tpl.format : 'PDF');
        setPeriod(tpl.period || '12m');
        setActiveTemplate(tpl.id);
        showToast(`Applied template: ${tpl.name}`);
    };

    const handleSaveTemplate = async () => {
        const name = (savingName || '').trim();
        if (!name) { setSavingName(''); return; }
        // Timestamped id so deletes can never make a later save regenerate an
        // existing id and silently overwrite a same-named template.
        const tpl = { id: `custom-${slugify(name)}-${Date.now()}`, name, title, format, period, sections: order, drill: [...withDrill] };
        try {
            await saveExportTemplates({ tenancyId, templates: [...customTemplates, tpl] }).unwrap();
            setSavingName(null);
            showToast(`Saved template: ${name}`);
        } catch {
            showToast('Could not save the template - please try again');
        }
    };

    const handleDeleteTemplate = async (id) => {
        try {
            await saveExportTemplates({ tenancyId, templates: customTemplates.filter((t) => t.id !== id) }).unwrap();
            if (activeTemplate === id) setActiveTemplate(null);
            showToast('Template deleted');
        } catch {
            showToast('Could not delete the template - please try again');
        }
    };

    const selectedItems = useMemo(() => assembleSections(order, withDrill, { benchmarkOn, period }, live), [order, withDrill, benchmarkOn, period, live]);

    const handleGenerate = async () => {
        if (selectedItems.length === 0) return;
        const { content, mime, filename } = await generateDocument(format, title, selectedItems, { brand, periodLabel: periodLabel(period) });
        downloadBlob(content, mime, filename);
        logExport({ kind: 'document', via: 'download', title, format, period, sectionIds: order, drillIds: [...withDrill], sectionCount: selectedItems.length });
        showToast(`Downloaded ${filename}`);
    };

    const handleEmail = () => {
        if (!emailTo.trim() || selectedItems.length === 0) return;
        logExport({ kind: 'document', via: 'email', title, format, period, sectionIds: order, drillIds: [...withDrill], sectionCount: selectedItems.length, recipients: [emailTo.trim()] });
        showToast(`Queued ${documentFilename(format, title)} for delivery to ${emailTo.trim()}`);
        setEmailTo('');
    };

    return (
        <Box sx={{ p: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>Export builder</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                    Start from a template or build your own. Tick the data to include, set the time period, preview it live, then download in any format or schedule automatic delivery.
                </Typography>
            </Box>

            {/* 1. Templates */}
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>Templates</Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Provided layouts and your saved templates. Click to load.</Typography>
                    </Box>
                    {savingName === null ? (
                        <Button
                            size="small" variant="outlined"
                            startIcon={<BookmarkAddOutlinedIcon sx={{ fontSize: 15 }} />}
                            onClick={() => setSavingName('')}
                            disabled={selectedItems.length === 0}
                            sx={{ textTransform: 'none', fontSize: 11, borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' } }}
                        >
                            Save as template
                        </Button>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: field appears on user action, focus it for immediate typing
                                autoFocus
                                placeholder="Template name"
                                value={savingName}
                                onChange={(e) => setSavingName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTemplate(); if (e.key === 'Escape') setSavingName(null); }}
                                sx={{ ...INPUT_SX, width: 180 }}
                            />
                            <Button size="small" variant="contained" color="secondary" onClick={handleSaveTemplate} sx={{ textTransform: 'none', fontSize: 11 }}>Save</Button>
                            <Button size="small" onClick={() => setSavingName(null)} sx={{ minWidth: 0, p: 0.5, color: 'text.disabled' }}><CloseIcon sx={{ fontSize: 16 }} /></Button>
                        </Box>
                    )}
                </Box>
                <Box sx={{ p: 2, display: 'flex', gap: 1, overflowX: 'auto' }}>
                    {PROVIDED_TEMPLATES.map((t) => (
                        <TemplateCard key={t.id} tpl={t} active={activeTemplate === t.id} onApply={() => applyTemplate(t)} />
                    ))}
                    {customTemplates.length > 0 && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
                    {customTemplates.map((t) => (
                        <TemplateCard key={t.id} tpl={t} active={activeTemplate === t.id} onApply={() => applyTemplate(t)} onDelete={() => handleDeleteTemplate(t.id)} />
                    ))}
                </Box>
            </MainCard>

            {/* 2. Build */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 360px' }, gap: 1 }}>
                {/* Library */}
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>Available data</Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{order.length} selected · live data, last {periodLabel(period)}{liveLoading ? ' (loading)' : ''}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <Button
                                size="small"
                                onClick={() => {
                                    setActiveTemplate(null);
                                    const all = library.flatMap((g) => g.items.map((it) => it.id));
                                    setOrder((prev) => [...prev, ...all.filter((id) => !prev.includes(id))]);
                                }}
                                sx={{ fontSize: 11, textTransform: 'none', color: 'text.secondary' }}
                            >Select all</Button>
                            <Button size="small" onClick={() => { setOrder([]); setWithDrill(new Set()); setActiveTemplate(null); }} sx={{ fontSize: 11, textTransform: 'none', color: 'text.secondary' }}>Clear all</Button>
                        </Box>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                        {library.map((g) => (
                            <Box key={g.group} sx={{ mb: 2 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.75 }}>{g.group}</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.25 }}>
                                    {g.items.map((it) => (
                                        <TickRow key={it.id} checked={selected.has(it.id)} onToggle={() => toggle(it.id)} label={it.label} />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </MainCard>

                {/* Config */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>Your export</Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{selectedItems.length} sections · reorder below</Typography>
                        </Box>
                        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField size="small" fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={INPUT_SX} />
                            <FormatTabs value={format} onChange={setFormat} />
                            <Box>
                                <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.5 }}>Time period</Typography>
                                <PillTabs options={EXPORT_PERIODS} value={period} onChange={(p) => { setActiveTemplate(null); setPeriod(p); }} />
                            </Box>
                            <Divider sx={{ my: 1 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 280, overflowY: 'auto' }}>
                                {selectedItems.length === 0 && (
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled', textAlign: 'center', py: 2 }}>Tick data on the left to begin.</Typography>
                                )}
                                {selectedItems.map((it, idx) => (
                                    <Box key={it.id} sx={{ display: 'flex', flexDirection: 'column', px: 1, py: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '6px', gap: 0.25 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Button onClick={() => move(it.id, -1)} disabled={idx === 0} sx={{ minWidth: 0, p: 0, height: 14, color: 'text.secondary', '&:hover': { color: 'secondary.main' }, '&.Mui-disabled': { color: 'text.disabled', opacity: 0.35 } }}><KeyboardArrowUpIcon sx={{ fontSize: 14 }} /></Button>
                                                <Button onClick={() => move(it.id, 1)} disabled={idx === selectedItems.length - 1} sx={{ minWidth: 0, p: 0, height: 14, color: 'text.secondary', '&:hover': { color: 'secondary.main' }, '&.Mui-disabled': { color: 'text.disabled', opacity: 0.35 } }}><KeyboardArrowDownIcon sx={{ fontSize: 14 }} /></Button>
                                            </Box>
                                            <Typography sx={{ fontSize: 10, color: 'text.disabled', minWidth: 16 }}>{idx + 1}</Typography>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</Typography>
                                                <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>{it.group}</Typography>
                                            </Box>
                                            <Button size="small" onClick={() => toggle(it.id)} sx={{ minWidth: 0, p: 0.25, color: 'text.disabled' }}><CloseIcon sx={{ fontSize: 13 }} /></Button>
                                        </Box>
                                        {it.drillable && (
                                            <TickRow checked={it.withDrill} onToggle={() => toggleDrill(it.id)} label={it.drillLabel || 'Include drill-down data'} indent />
                                        )}
                                    </Box>
                                ))}
                            </Box>

                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                <Button variant="outlined" size="small" onClick={() => setPreviewOpen(true)} disabled={selectedItems.length === 0} startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />} sx={{ flex: 1, textTransform: 'none', fontSize: 12, borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' } }}>
                                    Preview
                                </Button>
                                <Button variant="contained" color="secondary" size="small" onClick={handleGenerate} disabled={selectedItems.length === 0} startIcon={<DownloadIcon sx={{ fontSize: 14 }} />} sx={{ flex: 1, textTransform: 'none', fontSize: 12 }}>
                                    Download {format}
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <TextField size="small" fullWidth placeholder="Email a one-off copy..." value={emailTo} onChange={(e) => setEmailTo(e.target.value)} sx={INPUT_SX} />
                                <Button variant="outlined" size="small" onClick={handleEmail} disabled={!emailTo.trim() || selectedItems.length === 0} startIcon={<EmailIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontSize: 11, whiteSpace: 'nowrap' }}>Email</Button>
                            </Box>
                        </Box>
                    </MainCard>

                    <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                        <Box sx={{ p: 2.5 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: 1 }}>Snapshot used</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {[
                                    ['Org risk score', `${live.risk?.orgScore ?? ORG_SCORE} (${live.risk?.orgScore != null ? bandFor(live.risk.orgScore)?.name : ORG_BAND?.name})`],
                                    benchmarkOn && ['Platform avg', live.benchmark?.platformAvg ?? BENCHMARK.platformAvg],
                                    benchmarkOn && ['Top 25%', live.benchmark?.top25 ?? BENCHMARK.top25],
                                    dwmOn && ['DWM credential leaks', live.dwmStats?.credMentions ?? '—'],
                                ].filter(Boolean).map(([k, v]) => (
                                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{k}</Typography>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>{v}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </MainCard>
                </Box>
            </Box>

            {/* Preview lives in a modal so it never crowds the build surface. */}
            <PreviewDialog
                open={previewOpen} onClose={() => setPreviewOpen(false)}
                format={format} setFormat={setFormat} title={title} sections={selectedItems}
                brand={brand} periodText={periodLabel(period)}
                onDownload={handleGenerate}
            />

            <Snackbar
                open={!!toast}
                autoHideDuration={3500}
                onClose={() => setToast('')}
                message={toast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}
